import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import './SentencePracticePage.css';

const DEMO_WORDS = [
  { English: 'to travel', Spanish: 'viajar' },
  { English: 'airport', Spanish: 'el aeropuerto' },
  { English: 'airplane ticket', Spanish: 'el boleto de avión' },
  { English: 'passport', Spanish: 'el pasaporte' },
  { English: 'hotel', Spanish: 'el hotel' },
  { English: 'reservation', Spanish: 'la reservación' },
  { English: 'to check in', Spanish: 'registrarse' },
  { English: 'to check out', Spanish: 'retirarse' },
  { English: 'bus', Spanish: 'el camión' },
  { English: 'taxi', Spanish: 'el taxi' },
];

const DEMO_QUESTIONS = [
  { sentence: 'Me gusta _____ a nuevos países.', answer: 'to travel', translation: 'I like to travel to new countries.' },
  { sentence: 'El _____ está muy lejos del centro.', answer: 'airport', translation: 'The airport is very far from downtown.' },
  { sentence: 'Necesito comprar un _____.', answer: 'airplane ticket', translation: 'I need to buy an airplane ticket.' },
  { sentence: 'No olvides tu _____ cuando viajas.', answer: 'passport', translation: 'Don’t forget your passport when you travel.' },
  { sentence: 'Reservé un _____ para esta noche.', answer: 'hotel', translation: 'I booked a hotel for tonight.' },
  { sentence: '¿Tienes una _____ para la cena?', answer: 'reservation', translation: 'Do you have a reservation for dinner?' },
  { sentence: 'Vamos a la recepción para _____.', answer: 'to check in', translation: 'Let’s go to the front desk to check in.' },
  { sentence: 'Es hora de _____.', answer: 'to check out', translation: 'It’s time to check out.' },
  { sentence: 'Tomaremos el _____ al centro.', answer: 'bus', translation: 'We will take the bus to downtown.' },
  { sentence: '¿Puedes llamar un _____ para mí?', answer: 'taxi', translation: 'Can you call a taxi for me?' },
];

function stripSpanishArticle(str) {
  return (str || '').replace(/^(el|la|los|las|un|una|unos|unas)\s+/i, '').trim();
}

const shuffleArray = arr => {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
};

function DemoMCQPracticePage() {
  const navigate = useNavigate();
  const { t, isRTL } = useLanguage();
  const [shuffledQuestions] = useState(() => shuffleArray(DEMO_QUESTIONS));
  const [mcqOptions] = useState(() =>
    shuffledQuestions.map(q => {
      const correctWord = DEMO_WORDS.find(w => w.English === q.answer);
      const correct = correctWord ? stripSpanishArticle(correctWord.Spanish) : '';
      const distractors = [];
      const correctIdx = DEMO_WORDS.findIndex(w => w.English === q.answer);
      for (let i = 1; distractors.length < 3; i++) {
        const distractorIdx = (correctIdx + i) % DEMO_WORDS.length;
        distractors.push(stripSpanishArticle(DEMO_WORDS[distractorIdx].Spanish));
      }
      const options = [correct, ...distractors];
      const shuffled = shuffleArray(options);
      const correctOptionIdx = shuffled.findIndex(opt => opt === correct);
      return { options: shuffled, correctOptionIdx, correct };
    })
  );

  const [userChoices, setUserChoices] = useState(Array(DEMO_QUESTIONS.length).fill(null));
  const [feedback, setFeedback] = useState(Array(DEMO_QUESTIONS.length).fill(null));
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [showHints, setShowHints] = useState(Array(DEMO_QUESTIONS.length).fill(false));

  const handleChoice = (qIdx, choiceIdx) => {
    if (isSubmitted) return;
    const newChoices = [...userChoices];
    newChoices[qIdx] = (userChoices[qIdx] === choiceIdx) ? null : choiceIdx;
    setUserChoices(newChoices);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const newFeedback = shuffledQuestions.map((q, idx) => {
      const userChoiceIdx = userChoices[idx];
      if (userChoiceIdx === null) return 'incorrect';
      const { correctOptionIdx } = mcqOptions[idx];
      return userChoiceIdx === correctOptionIdx ? 'correct' : 'incorrect';
    });
    setFeedback(newFeedback);
    setIsSubmitted(true);
  };

  return (
    <div className="sentence-practice-container" style={{ marginTop: '3.5rem' }}>
      <button onClick={() => navigate('/module/demo/group/0')} className="btn btn-secondary btn-back" style={{ marginBottom: '2rem' }}>
        {isRTL ? '→' : '←'} {t('backToWordGroup')}
      </button>
      <form onSubmit={handleSubmit}>
        <ol className="sentence-list">
          {shuffledQuestions.map((q, idx) => {
            const { options, correctOptionIdx, correct } = mcqOptions[idx];
            return (
              <li key={idx} className={`sentence-item${isSubmitted && feedback[idx] ? ' answered ' + feedback[idx] : ''}`} style={{ listStyle: 'none', marginBottom: '2rem' }}>
                <div className="item-content card" style={{ maxWidth: 600, margin: '0 auto', padding: '2rem', textAlign: 'center' }}>
                  {!showHints[idx] && (
                    <div
                      style={{
                        background: '#fff',
                        border: '1px solid #e0e0e0',
                        borderRadius: '6px',
                        padding: '0 1.1rem',
                        marginBottom: '1rem',
                        display: 'inline-block',
                        textAlign: 'center',
                        lineHeight: 0.95,
                        transition: 'background 0.18s',
                      }}
                      onMouseEnter={e => { e.currentTarget.style.background = '#f3f3f3'; }}
                      onMouseLeave={e => { e.currentTarget.style.background = '#fff'; }}
                    >
                      <button
                        type="button"
                        style={{
                          background: 'none',
                          border: 'none',
                          color: '#666',
                          fontSize: '0.97rem',
                          padding: 0,
                          margin: 0,
                          fontWeight: 400,
                          cursor: 'pointer',
                          outline: 'none',
                        }}
                        onClick={() => {
                          const newShowHints = [...showHints];
                          newShowHints[idx] = true;
                          setShowHints(newShowHints);
                        }}
                      >
                        {t('showHint')}
                      </button>
                    </div>
                  )}
                  {showHints[idx] && (
                    <p className="translation-hint" style={{ textAlign: 'center' }}>
                      {t('hint')}: "{q.translation}"
                    </p>
                  )}
                  <label className="sentence-text" style={{ display: 'block', fontSize: '1.1rem', marginBottom: '1.2rem' }}>
                    {idx + 1}. {(() => {
                      const parts = q.sentence.split('_____');
                      return [
                        parts[0],
                        <span className="mcq-blank" key="blank" style={{ display: 'inline-block', minWidth: 60, borderBottom: '2px solid #bbb', margin: '0 0.3em', height: '1.1em', verticalAlign: 'middle' }}></span>,
                        parts[1]
                      ];
                    })()}
                  </label>
                  <div className="mcq-choices responsive-mcq-choices">
                    {options.map((choice, cIdx) => {
                      let btnClass = 'mcq-choice-btn';
                      if (!isSubmitted) {
                        if (userChoices[idx] === cIdx) btnClass += ' selected';
                      } else {
                        if (cIdx === correctOptionIdx) btnClass += ' correct';
                        else if (userChoices[idx] === cIdx && cIdx !== correctOptionIdx) btnClass += ' incorrect';
                      }
                      return (
                        <button
                          type="button"
                          key={cIdx}
                          className={btnClass}
                          onClick={() => handleChoice(idx, cIdx)}
                          disabled={isSubmitted}
                          style={{
                            width: 180,
                            height: 48,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '1rem',
                            borderRadius: 8,
                            textAlign: 'center',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            padding: 0,
                            transition: 'all 0.2s ease',
                          }}
                        >
                          {choice}
                        </button>
                      );
                    })}
                  </div>
                  {isSubmitted && (
                    <div className={`feedback-text ${feedback[idx]}`} style={{ marginTop: '1rem', fontWeight: 500 }}>
                      {feedback[idx] === 'correct'
                        ? t('correct')
                        : `${t('incorrect')} ${correct}`}
                    </div>
                  )}
                </div>
              </li>
            );
          })}
        </ol>
        <div className="practice-actions">
          {!isSubmitted ? (
            <button type="submit" className="btn btn-primary">
              {t('checkAnswers')}
            </button>
          ) : (
            <button type="button" className="btn btn-primary" onClick={() => navigate('/module/demo/group/0')}>
              {t('backToWordGroup')}
            </button>
          )}
        </div>
      </form>
    </div>
  );
}

export default DemoMCQPracticePage;
